import { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { ProjectModel } from 'api/dto/Project.dto';
import { PulseTemplateModel } from 'api/dto/PulseTemplate.dto';
import { Resource } from 'model/Resource';
import { ProjectState } from 'reduxStore/project/initialState';
import { addAsyncCasesNew } from 'shared/utils/redux';

import {
  fetchCurrentPulseProject,
  getPulseProject,
  getPulseProjectFast,
  refreshPulseProjectData,
  createPulseProject,
  changePulseFrequency,
  fetchSubProjectsList,
  fetchLiveAndScheduledSurveys,
  renamePulseProject,
} from './actions';

const renameIn = (projects: ProjectModel[] | null | undefined, projectId: number, name: string) => {
  projects?.forEach((project) => {
    if (project.id === projectId) {
      project.name = name;
    }
  });
};

const isLatestRequest = (state: ProjectState.Domain, action: { meta: { requestId: string } }) =>
  action.meta.requestId === state.pulseTemplateRequestId;

export const pulseProjectReducer = (builder: ActionReducerMapBuilder<ProjectState.Domain>) => {
  builder
    .addCase(fetchCurrentPulseProject.fulfilled, (state, action) => {
      state.currentResource = Resource.createResolvedResource({ data: action.payload });
      state.isPulseTemplateFullyLoaded = true;
    })
    .addCase(fetchCurrentPulseProject.rejected, (state, action) => {
      state.currentResource = Resource.createRejectedResource({ error: action.error });
    })

    .addCase(getPulseProjectFast.pending, (state, action) => {
      state.pulseTemplateRequestId = action.meta.requestId;
    })
    .addCase(getPulseProjectFast.fulfilled, (state, action) => {
      if (!isLatestRequest(state, action)) {
        return;
      }
      state.currentResource = Resource.createResolvedResource({ data: action.payload });
      state.isPulseTemplateFullyLoaded = false;
    })
    .addCase(getPulseProjectFast.rejected, (state, action) => {
      if (!isLatestRequest(state, action) || action.meta.aborted) {
        return;
      }
      state.currentResource = Resource.createRejectedResource({ error: action.error });
    })

    .addCase(getPulseProject.pending, (state, action) => {
      state.pulseTemplateRequestId = action.meta.requestId;
    })
    .addCase(getPulseProject.fulfilled, (state, action) => {
      if (!isLatestRequest(state, action)) {
        return;
      }
      state.currentResource = Resource.createResolvedResource({ data: action.payload });
      state.isPulseTemplateFullyLoaded = true;
    })
    .addCase(getPulseProject.rejected, (state, action) => {
      if (!isLatestRequest(state, action) || action.meta.aborted) {
        return;
      }
      state.currentResource = Resource.createRejectedResource({ error: action.error });
    })

    .addCase(refreshPulseProjectData.fulfilled, (state, action) => {
      const currentProject = state.currentResource.data;
      if (PulseTemplateModel.isPulseTemplateModel(currentProject)) {
        state.currentResource.data = {
          ...currentProject,
          project: action.payload,
        };
      } else {
        state.currentResource = Resource.createResolvedResource({ data: action.payload });
      }
    })

    .addCase(fetchSubProjectsList.pending, (state) => {
      state.subProjects.list = Resource.createPendingResource();
    })
    .addCase(fetchSubProjectsList.fulfilled, (state, action) => {
      state.subProjects.list = Resource.createResolvedResource({ data: action.payload.list });
      state.subProjects.pagination = action.payload.pagination;
    })
    .addCase(fetchSubProjectsList.rejected, (state, action) => {
      state.subProjects.list = Resource.createRejectedResource({ error: action.error });
      state.subProjects.pagination = ProjectState.INITIAL_DOMAIN['subProjects'].pagination;
    })

    .addCase(createPulseProject.fulfilled, (state, action) => {
      state.currentResource = Resource.createResolvedResource({ data: action.payload });
      state.isPulseTemplateFullyLoaded = true;
    })

    .addCase(changePulseFrequency.fulfilled, (state, action) => {
      state.currentResource = Resource.createResolvedResource({ data: action.payload });
      state.isPulseTemplateFullyLoaded = true;
    })

    .addCase(renamePulseProject, (state, action) => {
      const { projectId, name } = action.payload;
      const currentProject = state.currentResource.data;
      if (PulseTemplateModel.isPulseTemplateModel(currentProject)) {
        renameIn(currentProject.pulseProjects, projectId, name);
        renameIn(currentProject.pulseOnDemandPolls, projectId, name);
      }
      const { subProjects } = state;
      renameIn(subProjects?.list?.data, projectId, name);
      renameIn(subProjects?.liveAndScheduled?.data, projectId, name);
      if (subProjects?.current?.data?.id === projectId) {
        subProjects.current.data.name = name;
      }
    });

  addAsyncCasesNew(builder, fetchLiveAndScheduledSurveys, (state, action) => {
    state.subProjects.liveAndScheduled = action.payload;
  });
};
